// Notes SC5 L3 — the header session picker (mock v3): which session a note is for.
//   • the chip (📅 date · time + type, or 📝 General note · written {date} + No session) — painted in place;
//   • the menu: two capped reads when it opens (+1 by id for an older linked session; this patient's own appointments, not soft-deleted; personal blocks
//     have no patient so the patient filter excludes them; cancelled / no-show stay), grouped Today / Earlier (10
//     most recent) / Upcoming (next 5), statuses from memory;
//   • the move dialog (move this note / start a new one), and the auto-pick for a new note (from memory, no request).
import { noteSession, noteDateLabel, noteSessionType, computeNoteChip } from './helpers.js';
import { notesMoveTo, notesCreateLocal } from './editor.js';

const H = () => window.HUD || {};
const _hm = (t) => { const m = String(t || '').match(/(\d{1,2}):(\d{2})/); return m ? m[1].padStart(2,'0')+':'+m[2] : ''; };
const _min = (hm) => { const m = String(hm||'').match(/^(\d{2}):(\d{2})$/); return m ? (+m[1])*60+(+m[2]) : null; };
// Start / end minutes of an appointment: 'HH:MM – HH:MM' (en dash), or time + end_time, else start + 60.
function _span(a){
  const parts = String(a.time||'').split(/\s*[–-]\s*/);
  const s = _min(_hm(parts[0]));
  let e = _min(_hm(a.endTime||a.end_time||'')); if(e==null && parts[1]) e = _min(_hm(parts[1]));
  if(s!=null && (e==null || e<=s)) e = s+60;
  return { s, e };
}
const _myId = () => { const s = H().session; return s ? (s.specialistId || s.id || null) : null; };
const _cur = (note) => (H().DB.vaultNotes || []).find(n => n.id === note.id) || note;
// The note already written for a session (memory: this specialist's notes list).
function _noteFor(apptId, exceptId){
  const me = _myId();
  return (H().DB.vaultNotes || []).find(n => n.appointmentId === apptId && n.id !== exceptId && n.specialistId === me && n.title !== '__DELETED__') || null;
}
// ── SC5 L4: the appointment popup's notes button (state from memory — no request) ──
// null → no button: not the specialist's own session, or no patient (a personal block), or soft-deleted.
export function noteForAppointment(apptId){ return apptId ? _noteFor(apptId, null) : null; }
export function aptNoteState(apt){
  const me = _myId();
  if(!apt || !me || apt.specialistId !== me || (!apt.childId && !apt.specialistPatientId) || apt.deletedAt) return null;
  const n = _noteFor(apt.id, null);
  if(!n) return { note:null, tag:null };
  // precedence Sent > Locked > the cards' own tag (pending / draft / saved) — the cards' existing keys
  const tag = n.published ? { label:H().T('vn2_state_published'), bg:'#dcfce7', fg:'#166534' } : computeNoteChip(n, { isOpenNote:false, withTime:false });
  return { note:n, tag };
}
const _fmtDay = (ymd, withYear) => { const d = new Date(String(ymd)+'T12:00:00'); if(isNaN(d)) return ymd || '';
  return H()._locDate(d, withYear ? {day:'numeric',month:'short',year:'numeric'} : {weekday:'short',day:'numeric',month:'short'}); };
const _sessLabel = (a) => _fmtDay(a.date, false) + (_hm(a.time) ? ' · '+_hm(a.time) : '');

// ── auto-pick (decision: SC5 L3 item 4) ──
// A new note from the patient page, among TODAY's own sessions for this patient that have no note:
// in progress → ended most recently today → later today (the next one) → none (general). From memory.
// Cancelled / no-show sessions are not auto-picked (they stay pickable in the menu).
const _SKIP_AUTO = new Set(['patient_cancelled','cancelled','no_show']);
export function autoPickSession({ childId=null, specialistPatientId=null }={}){
  const me = _myId(), today = H()._localToday();
  if(!me || (!childId && !specialistPatientId)) return null;
  const now = new Date(), nowMin = now.getHours()*60 + now.getMinutes();
  const cands = (H().DB.appointments || []).filter(a => a && a.specialistId === me && !a.deletedAt && a.date === today
    && (childId ? a.childId === childId : a.specialistPatientId === specialistPatientId)
    && !_SKIP_AUTO.has(a.attendance) && !_noteFor(a.id, null))
    .map(a => ({ a, ..._span(a) })).filter(x => x.s != null);
  const live = cands.filter(x => x.s <= nowMin && nowMin < x.e).sort((x,y) => y.s - x.s)[0];
  const ended = cands.filter(x => x.e <= nowMin).sort((x,y) => y.e - x.e)[0];
  const later = cands.filter(x => x.s > nowMin).sort((x,y) => x.s - y.s)[0];
  const pick = live || ended || later;
  return pick ? { id:pick.a.id, date:pick.a.date, time:pick.a.time, title:pick.a.title||'' } : null;
}

// ── the chip ──
export function paintSessionChip(btn, note){
  const { el, T } = H();
  const cur = _cur(note), s = noteSession(cur);
  btn.classList.toggle('vn2-sess-gen', !s);
  if(s){
    const typ = noteSessionType(cur), t = _hm(s.time);
    btn.replaceChildren(
      el('span',{class:'vn2-sc'},['📅 ', el('b',{},[el('bdi',{dir:'auto'},[_fmtDay(s.date, true)])]), ...(t?[' · ', el('bdi',{dir:'ltr'},[t])]:[])]),
      ...(typ ? [el('span',{class:'vn2-sc vn2-sc-type'},[typ])] : []),
      el('span',{class:'vn2-car','aria-hidden':'true'},['▾']));
  } else {
    btn.replaceChildren(
      el('span',{class:'vn2-sc'},['📝 ', el('b',{},[T('notes_pick_general')]), ' · ', T('notes_chip_written',{date:noteDateLabel(cur)})]),
      el('span',{class:'vn2-sc vn2-sc-type'},[T('notes_chip_no_session')]),
      el('span',{class:'vn2-car','aria-hidden':'true'},['▾']));
  }
}

// ── the menu ──
let _menu = null, _menuBtn = null, _menuSeq = 0;
export function closeSessionMenu(){
  if(_menu){ _menu.remove(); _menu = null; }
  if(_menuBtn){ _menuBtn.setAttribute('aria-expanded','false'); _menuBtn = null; }
  _menuSeq++; // a late response for a closed menu is dropped
}
export function sessionMenuOpen(){ return !!(_menu && _menu.isConnected); }

// Capped reads per menu open: this patient's own appointments (mirrors the link guard's patient rule). Queue item 20:
// today + earlier come from one read newest-first (room for _PICK_TODAY_ROOM sessions today on top of the 10 earlier
// that _group keeps), upcoming from one read soonest-first (_group keeps 5; the slack leaves the tie-break on a shared
// date to _group's own time sort, not the DB's text order). The open note's own session is read by id only when it
// falls outside both.
const _PICK_EARLIER = 10, _PICK_TODAY_ROOM = 15, _PICK_UPCOMING = 5, _PICK_UPCOMING_ROOM = 5;
async function _fetchSessions(cur){
  const me = _myId();
  if(!me || (!cur.childId && !cur.specialistPatientId)) return { rows:[] };
  const base = () => {
    const q = H()._supa.from('appointments').select('id,date,time,end_time,title,attendance')
      .eq('specialist_id', String(me)).is('deleted_at', null);
    return cur.childId ? q.eq('child_id', String(cur.childId)) : q.eq('specialist_patient_id', String(cur.specialistPatientId));
  };
  const today = H()._localToday();
  try{
    const [past, next] = await Promise.all([
      base().lte('date', today).order('date', { ascending:false }).order('time', { ascending:false }).limit(_PICK_EARLIER + _PICK_TODAY_ROOM),
      base().gt('date', today).order('date', { ascending:true }).order('time', { ascending:true }).limit(_PICK_UPCOMING + _PICK_UPCOMING_ROOM),
    ]);
    const error = past.error || next.error;
    if(error){ console.error('❌ session picker appointments:', error); return { error }; }
    const rows = [...(past.data || []), ...(next.data || [])];
    if(noteSession(cur) && cur.appointmentId && !rows.some(r => r.id === cur.appointmentId)){
      const { data, error:e2 } = await base().eq('id', String(cur.appointmentId));
      if(e2){ console.error('❌ session picker appointments:', e2); return { error:e2 }; }
      rows.push(...(data || []));
    }
    return { rows };
  }catch(e){ console.error('❌ session picker appointments:', e); return { error:e }; }
}

function _group(rows, cur){
  const today = H()._localToday();
  const byTime = (a,b) => _hm(a.time).localeCompare(_hm(b.time));
  const todayRows = rows.filter(r => r.date === today).sort(byTime);
  const earlier = rows.filter(r => r.date < today).sort((a,b) => b.date.localeCompare(a.date) || byTime(b,a)).slice(0,10);
  const upcoming = rows.filter(r => r.date > today).sort((a,b) => a.date.localeCompare(b.date) || byTime(a,b)).slice(0,5);
  // The open note's own session is always listed, even outside the windows.
  const s = noteSession(cur);
  if(s && cur.appointmentId){
    const r = rows.find(x => x.id === cur.appointmentId);
    if(r && ![...todayRows, ...earlier, ...upcoming].includes(r)){
      if(r.date < today){ earlier.push(r); earlier.sort((a,b) => b.date.localeCompare(a.date) || byTime(b,a)); }
      else { upcoming.push(r); upcoming.sort((a,b) => a.date.localeCompare(b.date) || byTime(a,b)); }
    }
  }
  return [['notes_pick_today', todayRows], ['notes_pick_earlier', earlier], ['notes_pick_upcoming', upcoming]];
}

export function toggleSessionMenu(btn, note, ctl){
  if(sessionMenuOpen() && _menuBtn === btn){ closeSessionMenu(); return; }
  closeSessionMenu();
  const { el, T } = H();
  const root = btn.closest('.vn2-root') || document.body;
  const menu = el('div',{class:'vn2-sess-menu',role:'menu'});
  menu.appendChild(el('div',{class:'vn2-sm-loading'},['…']));
  root.appendChild(menu);
  _menu = menu; _menuBtn = btn; btn.setAttribute('aria-expanded','true');
  _place(menu, btn);
  // Outside click closes (the listener lives on the editor root, which a re-render replaces).
  const onDown = (e) => { if(!_menu || _menu.contains(e.target) || btn.contains(e.target)) return; closeSessionMenu(); root.removeEventListener('mousedown', onDown, true); };
  root.addEventListener('mousedown', onDown, true);
  const seq = ++_menuSeq, cur = _cur(note);
  _fetchSessions(cur).then(res => {
    if(seq !== _menuSeq || !_menu) return;
    _fill(menu, res, cur, ctl);
    _place(menu, btn);
  });
}

function _place(menu, btn){
  const r = btn.getBoundingClientRect(), vw = window.innerWidth, w = Math.min(340, vw - 16);
  menu.style.width = w + 'px';
  menu.style.top = Math.round(r.bottom + 6) + 'px';
  const rtl = getComputedStyle(btn).direction === 'rtl';
  const left = rtl ? Math.max(8, r.right - w) : Math.min(vw - w - 8, Math.max(8, r.left));
  menu.style.left = Math.round(left) + 'px';
  menu.style.maxHeight = Math.max(160, Math.min(window.innerHeight * 0.7, window.innerHeight - r.bottom - 16)) + 'px';
}

function _fill(menu, res, cur, ctl){
  const { el, T } = H();
  menu.replaceChildren();
  if(res.error){ menu.appendChild(el('div',{class:'vn2-sm-err'},[T('notes_err_load')])); }
  else {
    for(const [key, rows] of _group(res.rows, cur)){
      if(!rows.length) continue;
      menu.appendChild(el('div',{class:'vn2-sm-h'},[T(key)]));
      for(const a of rows){
        const isCur = !!noteSession(cur) && cur.appointmentId === a.id;
        const n = isCur ? null : _noteFor(a.id, cur.id);
        const st = isCur ? el('span',{class:'vn2-sm-st vn2-sm-cur'},[T('notes_pick_current')])
          : n ? el('span',{class:'vn2-sm-st vn2-sm-has'},[computeNoteChip(n,{isOpenNote:false,withTime:false}).label])
          : el('span',{class:'vn2-sm-st'},[T('notes_pick_none')]);
        menu.appendChild(_item([el('b',{},[el('bdi',{dir:'auto'},[_sessLabel(a)])]), ...(a.title?[el('small',{},[a.title])]:[])], st, isCur, () => _pick(cur, a, ctl)));
      }
    }
  }
  const gCur = !noteSession(cur);
  const foot = el('div',{class:'vn2-sm-foot'});
  foot.appendChild(_item([el('b',{},['📝 ', T('notes_pick_general')]), el('small',{},[T('notes_pick_general_sub')])],
    gCur ? el('span',{class:'vn2-sm-st vn2-sm-cur'},[T('notes_pick_current')]) : null, gCur, () => _pick(cur, null, ctl)));
  menu.appendChild(foot);
}
function _item(label, st, isCur, onPick){
  const { el } = H();
  const it = el('div',{class:'vn2-sm-i'+(isCur?' vn2-sm-i-cur':''),role:'menuitem',tabindex:'0'});
  it.appendChild(el('span',{class:'vn2-sm-l'},label));
  if(st) it.appendChild(st);
  it.onclick = (e) => { e.stopPropagation(); onPick(); };
  it.onkeydown = (e) => { if(e.key==='Enter'||e.key===' '){ e.preventDefault(); onPick(); } };
  return it;
}

// ── choosing a row ──
function _pick(cur0, a, ctl){
  closeSessionMenu();
  const cur = _cur(cur0), curSess = noteSession(cur);
  if(!a){ if(!curSess) return; }                                   // general → already general
  else if(curSess && cur.appointmentId === a.id) return;          // this note's own session
  if(a){ const n = _noteFor(a.id, cur.id); if(n){ ctl.openNote(n.id); return; } } // a session with a note → open it
  _moveDialog(cur, a, ctl);
}

function _moveDialog(cur, a, ctl){
  const { el, T, S, openModal, mkBtn } = H();
  const locked = !!cur.locked;
  const title = a ? T('notes_move_title',{session:_sessLabel(a)}) : T('notes_move_title_general');
  // The dialog is a body-level .overlay (re()'s strip target) → hold the render lock while it is open.
  S._suppressRefreshCount = (S._suppressRefreshCount||0) + 1;
  openModal(title, (mb, close) => {
    mb.appendChild(el('div',{class:'vn2-move-body'},[T(locked ? 'notes_move_body_locked' : 'notes_move_body')]));
    const col = el('div',{class:'vn2-move-btns'});
    const mv = mkBtn(T(a ? 'notes_move_here' : 'notes_move_general'),'btn-md btn-primary',() => { if(locked) return; close(); _move(cur, a, ctl); });
    if(locked){ mv.disabled = true; mv.setAttribute('aria-disabled','true'); }
    col.appendChild(mv);
    col.appendChild(mkBtn(T(a ? 'notes_new_for_session' : 'notes_new_general'),'btn-md btn-ghost',() => { close(); _startNew(cur, a, ctl); }));
    col.appendChild(mkBtn(T('btn_cancel'),'btn-md btn-ghost',close));
    mb.appendChild(col);
  }, 420, () => { S._suppressRefreshCount = Math.max(0, (S._suppressRefreshCount||1) - 1); });
}

async function _move(cur, a, ctl){
  const { T, toast } = H();
  const res = await notesMoveTo(a ? a.id : null, a ? { id:a.id, date:a.date, time:a.time, title:a.title||'', deleted_at:null } : null);
  if(res.r === 'ok' || res.r === 'conflict' || res.r === 'deleted') return; // ok → the chip repaints; conflict/deleted → the banner
  if(res.code === '23505' && a){ await _openTaken(a.id, ctl); return; }  // the menu was stale: that session got a note meanwhile
  try{ toast(T('err_generic'),'error'); }catch(_){}
}

// That session's note (created elsewhere after the menu's memory) → load it and open it.
async function _openTaken(apptId, ctl){
  const { T, toast, DB, _supa } = H();
  let row = null;
  try{ const { data, error } = await _supa.from('vault_notes').select('id,child_id,specialist_patient_id,title,summary,is_draft,locked,published,pending_publish,last_saved,specialist_id,created_at,parent_read_at,homework_prompt_shown,appointment_id')
      .eq('appointment_id', apptId).neq('title','__DELETED__');
    if(error) console.error('❌ session note lookup:', error); else row = data && data[0]; }catch(e){ console.error('❌ session note lookup:', e); }
  if(!row){ try{ toast(T('err_generic'),'error'); }catch(_){} return; }
  if(!(DB.vaultNotes||[]).some(n => n.id === row.id)){
    DB.vaultNotes = [{ id:row.id, childId:row.child_id, specialistPatientId:row.specialist_patient_id||null, title:row.title||'', content:'', summary:row.summary||'',
      isDraft:row.is_draft!==false, locked:!!row.locked, published:!!row.published, pendingPublish:!!row.pending_publish, lastSaved:row.last_saved||'',
      specialistId:row.specialist_id, date:H()._localDateOf(row.created_at), parentReadAt:row.parent_read_at||null,
      homeworkPromptShown:!!row.homework_prompt_shown, writtenAt:row.created_at||null, appointmentId:row.appointment_id||null, session:null }, ...(DB.vaultNotes||[])];
  }
  try{ toast(T('notes_session_taken')); }catch(_){}
  ctl.openNote(row.id);
}

// "Start a new note": the open note is saved first (ctl.openNote → the switch flushes it); the new note is
// local — no row until it has text, and left empty it simply disappears.
function _startNew(cur, a, ctl){
  const nn = notesCreateLocal({ childId:cur.childId||null, specialistPatientId:cur.specialistPatientId||null,
    appointment: a ? { id:a.id, date:a.date, time:a.time, title:a.title||'' } : null });
  ctl.openNote(nn.id, { dropIfRefused:true });
}
